/**
 * BulkCreateContext - Global context for bulk create operation management
 *
 * Provides access to bulk create operations across the application,
 * allowing any component to start operations and track their progress.
 */

import * as React from 'react';

import { useBulkCreateOperation } from '@/hooks/useBulkCreateOperation';
import type { StartExternalTaskBulkCreateRequest } from '@/services/api/generated/externalTaskService';

interface BulkCreateContextValue {
  /** Start a new bulk create operation */
  startOperation: (
    request: StartExternalTaskBulkCreateRequest,
  ) => Promise<string>;
  /** Currently active operation IDs */
  activeOperationIds: string[];
  /** Show toast for an existing operation */
  showToastForOperation: (operationId: string) => void;
  /** Dismiss an operation toast */
  dismissOperation: (operationId: string) => void;
  /** Whether an operation is currently starting */
  isStarting: boolean;
}

const BulkCreateContext = React.createContext<BulkCreateContextValue | null>(
  null,
);

export const BulkCreateProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const bulkCreateOperations = useBulkCreateOperation();

  return (
    <BulkCreateContext.Provider value={bulkCreateOperations}>
      {children}
    </BulkCreateContext.Provider>
  );
};

export const useBulkCreate = (): BulkCreateContextValue => {
  const context = React.useContext(BulkCreateContext);
  if (!context) {
    throw new Error('useBulkCreate must be used within a BulkCreateProvider');
  }
  return context;
};
